import type { MediaMessageType, Message } from '@/interfaces/message'
import type { Ticket } from '@/interfaces/ticket'
import { httpClient } from '@/lib/api'

export type ListTicketsResponse = {
  tickets: Array<Ticket>
}

export type ListMessagesResponse = {
  messages: Array<Message>
}

export type CreateManualTicketsPayload = {
  contactIds: number[]
  whatsappId: string
  departmentId: number
}

export type CreateManualTicketsResponse = {
  created: Array<Ticket>
  reused: Array<Ticket>
}

export class TicketService {
  private static readonly mediaTypeMap: Record<string, MediaMessageType> = {
    text: 'text',
    textmessage: 'text',
    imagemessage: 'image',
    image: 'image',
    videomessage: 'video',
    video: 'video',
    audiomessage: 'audio',
    audio: 'audio',
    ptt: 'ptt',
    pttmessage: 'ptt',
    documentmessage: 'document',
    document: 'document',
    stickermessage: 'sticker',
    sticker: 'sticker',
    templatemessage: 'template',
    template: 'template',
    reaction: 'reaction',
    location: 'location',
  }

  public static async listInProgress() {
    const { data } =
      await httpClient().get<ListTicketsResponse>(`/ticket/in-progress`)
    return data.tickets
  }

  public static async listAgent() {
    const { data } =
      await httpClient().get<ListTicketsResponse>(`/ticket/agent`)
    return data.tickets
  }

  public static async listPending() {
    const { data } =
      await httpClient().get<ListTicketsResponse>(`/ticket/pending`)
    return data.tickets
  }

  public static async listFinished() {
    const { data } =
      await httpClient().get<ListTicketsResponse>(`/ticket/finished`)
    return data.tickets
  }

  public static async getById(id: number) {
    const { data } = await httpClient().get<Ticket>(`/ticket/${id}`)
    return data
  }

  public static async finishById(id: number) {
    const { data } = await httpClient().patch(`/ticket/${id}/close`)
    return data
  }

  public static async listMessagesByTicketId(id: number) {
    const { data } = await httpClient().get<ListMessagesResponse>(
      `/ticket/${id}/messages`
    )
    return (data.messages || []).map((message) =>
      TicketService.normalizeMessage(message)
    )
  }

  public static async sendMessage(ticketId: number, payload: FormData) {
    const { data } = await httpClient().post(
      `/ticket/${ticketId}/send-message`,
      payload
    )
    return TicketService.normalizeMessage(data)
  }

  public static async transferToDepartment(
    ticketId: number,
    departmentId: number
  ) {
    const { data } = await httpClient().patch(`/ticket/${ticketId}/transfer`, {
      departmentId,
    })
    return data
  }

  public static async transferToAttendant(
    ticketId: number,
    attendantId: string
  ) {
    const { data } = await httpClient().patch(`/ticket/${ticketId}/transfer`, {
      attendantId,
    })
    return data
  }

  public static async joinChat(ticketId: number) {
    const { data } = await httpClient().patch<Ticket>(
      `/ticket/${ticketId}/join-chat`
    )
    return data
  }

  public static async close(ticketId: number) {
    const { data } = await httpClient().patch<Ticket>(
      `/ticket/${ticketId}/close`
    )
    return data
  }

  public static async create(payload: CreateManualTicketsPayload) {
    const { data } = await httpClient().post<CreateManualTicketsResponse>(
      `/ticket`,
      payload
    )
    return data
  }

  private static normalizeMessage(message: Message): Message {
    const safeTimestamp = this.normalizeTimestamp(
      message.timestamp,
      message.createdAt
    )
    const createdAt = this.normalizeTimestamp(message.createdAt, safeTimestamp)
    const updatedAt = this.normalizeTimestamp(message.updatedAt, createdAt)
    const normalizedType = this.normalizeMediaType(
      message.mediaType || message.type
    )

    return {
      ...message,
      timestamp: safeTimestamp,
      createdAt,
      updatedAt,
      type: normalizedType,
      mediaUrl: message.mediaUrl ?? '',
      mediaKey: message.mediaKey ?? '',
      mediaThumbnailUrl: message.mediaThumbnailUrl ?? '',
      mediaType: normalizedType,
      filename: message.filename ?? '',
      caption: message.caption ?? '',
      content: message.content ?? '',
    }
  }

  private static normalizeTimestamp(
    timestamp?: string | number,
    fallback?: string
  ) {
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toISOString()
    }
    if (timestamp) {
      return timestamp
    }
    return fallback ?? new Date().toISOString()
  }

  private static normalizeMediaType(type?: string | null): MediaMessageType {
    const key = (type || 'text').toLowerCase()
    return this.mediaTypeMap[key] ?? 'text'
  }
}
